import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import { APP_NAME } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import type { ReportGroupBy } from '@/types/report';
import styles from '../Dashboard.module.scss';

interface DashboardHeaderProps {
  dateRangeValue: [Dayjs, Dayjs];
  groupBy: ReportGroupBy;
  roleLabel?: string;
  canViewOrders?: boolean;
  canViewReports?: boolean;
  onDateRangeChange: (dateFrom: string, dateTo: string) => void;
  onGroupByChange: (groupBy: ReportGroupBy) => void;
  onReset: () => void;
}

export function DashboardHeader({
  dateRangeValue,
  groupBy,
  roleLabel,
  canViewOrders = true,
  canViewReports = true,
  onDateRangeChange,
  onGroupByChange,
  onReset,
}: DashboardHeaderProps) {
  return (
    <header className={styles.topBar}>
      <div className={styles.topBarLeft}>
        <div className={styles.topBarTitleWrap}>
          <h1 className={styles.topBarTitle}>Dashboard</h1>
          <p className={styles.topBarSubtitle}>
            {roleLabel
              ? `${roleLabel} overview for ${APP_NAME}`
              : `Commerce overview for ${APP_NAME}`}
          </p>
        </div>
        <span className={styles.liveChip}>Live</span>
      </div>

      <div className={styles.topBarFilters}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Date range</span>
          <DatePicker.RangePicker
            value={dateRangeValue}
            format="YYYY-MM-DD"
            onChange={(values: [Dayjs | null, Dayjs | null] | null) => {
              if (!values?.[0] || !values?.[1]) return;
              onDateRangeChange(values[0].format('YYYY-MM-DD'), values[1].format('YYYY-MM-DD'));
            }}
            allowClear={false}
          />
        </div>

        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Group by</span>
          <Select
            value={groupBy}
            onChange={onGroupByChange}
            options={[
              { label: 'Daily', value: 'day' },
              { label: 'Weekly', value: 'week' },
              { label: 'Monthly', value: 'month' },
            ]}
            style={{ minWidth: 140 }}
          />
        </div>
      </div>

      <div className={styles.topBarActions}>
        <Button className={styles.outlineBtn} icon={<ReloadOutlined />} onClick={onReset}>
          Reset
        </Button>
        {canViewOrders ? (
          <Link to={ROUTES.ORDERS}>
            <Button className={styles.outlineBtn}>View orders</Button>
          </Link>
        ) : null}
        {canViewReports ? (
          <Link to={ROUTES.REPORTS}>
            <Button type="primary" className={styles.primaryBtn} icon={<BarChartOutlined />}>
              Full reports
            </Button>
          </Link>
        ) : null}
      </div>
    </header>
  );
}
